import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  FormControl,
  InputGroup,
  Button,
  Spinner,
  Card,
  Table,
  Form,
  Badge,
  Alert,
} from 'react-bootstrap';
import Loading from '../shared/loading/Loading';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useLoading } from '../../hooks/useLoading';
import {
  loadConfigurationFactors,
  shouldQueueProfitProductInventoryRefreshFromProfit,
} from '../../api/configuration';
import { Return, loadReturns, subscribeToReturns } from '../../api/returns';
import { isSelfHosted } from '../../config/infra';
import {
  approveReturnServer,
  rejectReturnServer,
  deleteReturnServer,
} from '../../api/returnsServer';
import {
  Search as FiSearch,
  Trash3 as FiTrash2,
  Plus as FiPlus,
} from 'react-bootstrap-icons';
import listStyles from '../shared/layout/dataListTable.module.scss';
import styles from './Returns.module.scss';
import ReturnForm from './ReturnForm';
import ReturnView from './ReturnView';
import ReturnRowActions from './ReturnRowActions';
import ReturnTrashBin from './ReturnTrashBin';
import {
  resolveOrderCreatorDisplay,
  pascalCaseWords,
  type OrderCreatorDisplayInfo,
} from '../orders/orderCreatorDisplay';
import SubscriptionGuard from '../shared/billing/SubscriptionGuard';
import { Organization, SUPPORTED_CURRENCIES } from '../../models/organization';
import { getReturnSourceSalesDisplay } from '../../models/return';
import type { ProfitPlus2k8OrgConfig } from '../../models/configuration';
import {
  formatDateEsFromFirestore,
  formatTimeEsFromFirestore,
} from '../../utils/format';
import { PANEL_PERMISSIONS } from '../../utils/panelPermissions';
import {
  PanelReadOnlyBanner,
  RequirePanelWrite,
} from '../auth/PanelWriteGate';
import { useCanWritePanelArea } from '../../contexts/PanelAccessContext';

function getApprovalStateInfo(state: string) {
  switch (state) {
    case 'pending':
      return { label: 'Pendiente', variant: 'warning' as const };
    case 'approved':
      return { label: 'Aprobado', variant: 'success' as const };
    case 'rejected':
      return { label: 'Rechazado', variant: 'danger' as const };
    default:
      return { label: 'Desconocido', variant: 'secondary' as const };
  }
}

function mapReturnDeleteError(error: any): string {
  const detail = String(error?.response?.data?.detail || error?.message || '').trim();
  const normalized = detail.toLowerCase();
  if (normalized.includes('client credit was already used or refunded')) {
    return 'No se puede eliminar la devolución porque el saldo a favor generado ya fue usado o reembolsado.';
  }
  if (normalized.includes('would make product inventory negative')) {
    return 'No se puede eliminar la devolución porque dejaría inventario negativo en uno o más productos.';
  }
  if (normalized.includes('cannot be restored')) {
    return 'No se puede restaurar una devolución aprobada eliminada porque sus efectos financieros ya fueron revertidos.';
  }
  if (normalized.includes('approved returns cannot be rejected')) {
    return 'No se puede rechazar una devolución ya aprobada. Elimínala para revertir inventario y efectos financieros.';
  }
  return detail || 'Algo salió mal';
}

function getReturnReference(returnObj: Return): string {
  if (returnObj.returnNumber) {
    return `#${returnObj.returnNumber}`;
  }
  if (returnObj.id) {
    return `${returnObj.id.substring(0, 8)}`;
  }
  return '#';
}

function getDisplayIvaAmount(returnObj: Return): number {
  const subtotal = Number(returnObj.subtotal) || 0;
  const total = Number(returnObj.total) || 0;
  const stored = Number(returnObj.ivaAmount) || 0;
  return Math.abs(subtotal + stored - total) > 0.01 ? total - subtotal : stored;
}


interface ReturnsProps {
  device: string;
  addToast: (message: string) => void;
  navStatus: string;
  organizationId: string;
  organization: Organization | null;
}

export default function Returns({
  device,
  addToast,
  navStatus,
  organizationId,
  organization,
}: ReturnsProps) {
  const { showConfirmation } = useConfirmationDialog();
  const { showLoading, hideLoading } = useLoading();
  const canWriteOrders = useCanWritePanelArea(PANEL_PERMISSIONS.ORDERS);
  const [returns, setReturns] = useState<Return[]>([]);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showReturnView, setShowReturnView] = useState(false);
  const [selectedReturnForView, setSelectedReturnForView] =
    useState<Return | null>(null);
  const [returnApprovalRequired, setReturnApprovalRequired] = useState(true);
  const [referenceCurrency, setReferenceCurrency] = useState('USD');
  const [currency, setCurrency] = useState('VES');
  const [screen, setScreen] = useState(0); // 0 = main list, 1 = trash bin
  const [loadMore, setLoadMore] = useState(false);
  const [lastDocument, setLastDocument] = useState<any>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [profitPlus2k8OrgConfig, setProfitPlus2k8OrgConfig] =
    useState<ProfitPlus2k8OrgConfig | null>(null);
  const [creatorDisplayByStored, setCreatorDisplayByStored] = useState<
    Record<string, OrderCreatorDisplayInfo>
  >({});

  const returnsListenerRef = useRef<any>(null);
  const loadReturnsRequestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const creatorDisplayFetchStartedRef = useRef<Set<string>>(new Set());

  const uniqueCreatorKeys = useMemo(
    () =>
      Array.from(
        new Set(
          returns
            .map(r => (r.creatorSalesPerson || '').trim())
            .filter(Boolean)
        )
      ),
    [returns]
  );

  const renderCreatorSalesPerson = useCallback(
    (raw: string | undefined) => {
      const s = (raw ?? '').trim();
      if (!s) return '—';
      const info = creatorDisplayByStored[s];
      if (!info) return pascalCaseWords(s);
      return (
        <span className="d-inline-flex align-items-center flex-wrap gap-1">
          <span>{info.name}</span>
          {info.isAdmin && (
            <Badge bg="secondary" className="small">
              Admin
            </Badge>
          )}
        </span>
      );
    },
    [creatorDisplayByStored]
  );

  const groupedNumberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const getCurrencySymbol = useCallback((code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  }, []);
  const formatMoney = useCallback(
    (amount: number, code: string = referenceCurrency): string => {
      const rounded = groupedNumberFormatter.format(Number(amount) || 0);
      const symbol = getCurrencySymbol(code);
      return symbol ? `${symbol} ${rounded}` : `${rounded}`;
    },
    [referenceCurrency, getCurrencySymbol]
  );

  // Load configuration to check if approval is required
  const loadConfiguration = useCallback(async () => {
    try {
      const config = await loadConfigurationFactors(organizationId);
      setReturnApprovalRequired(config.orderApprovalRequired || false);
      setReferenceCurrency(config.referenceCurrency || 'USD');
      setCurrency(config.currency || 'VES');
      setProfitPlus2k8OrgConfig(config.profitPlus2k8 ?? null);
    } catch (e) {
      console.error('Failed to load configuration:', e);
      setReturnApprovalRequired(true);
      setProfitPlus2k8OrgConfig(null);
    }
  }, [organizationId]);

  const showProfitPlusReturnsInfo = useMemo(
    () => shouldQueueProfitProductInventoryRefreshFromProfit(profitPlus2k8OrgConfig),
    [profitPlus2k8OrgConfig]
  );

  const loadReturnsList = useCallback(async () => {
    if (!organizationId) return;

    const requestId = ++loadReturnsRequestIdRef.current;
    setPending(true);

    try {
      const result = await loadReturns(organizationId, search || '', undefined, 10);
      if (requestId !== loadReturnsRequestIdRef.current) return;
      setReturns(result.returns);
      setLoadMore(result.hasMore);
      setLastDocument(result.lastDoc);
    } catch (error) {
      if (requestId !== loadReturnsRequestIdRef.current) return;
      console.log(error);
      addToast('Error al cargar devoluciones');
      setLoadMore(false);
    } finally {
      if (requestId === loadReturnsRequestIdRef.current) {
        setPending(false);
      }
    }
  }, [organizationId, search, addToast]);

  const getReturns = useCallback(() => {
    if (isSelfHosted) {
      void loadReturnsList();
      return;
    }

    if (!mountedRef.current) return;

    setPending(true);

    if (returnsListenerRef.current !== null) {
      returnsListenerRef.current(); // Clean the listener
    }

    returnsListenerRef.current = subscribeToReturns(
      organizationId,
      search || '',
      (returns, hasMore, lastDoc) => {
        if (mountedRef.current) {
          setReturns(returns);
          setLoadMore(hasMore);
          setLastDocument(lastDoc);
          setPending(false);
        }
      },
      error => {
        console.log(error);
        if (mountedRef.current) {
          setPending(false);
          setLoadMore(false);
        }
        addToast('Error al cargar devoluciones');
      },
      10
    );
  }, [search, organizationId, addToast, loadReturnsList]);

  // Effect for component mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    loadConfiguration();
    getReturns();

    return () => {
      mountedRef.current = false;

      if (returnsListenerRef.current !== null) {
        returnsListenerRef.current(); // Clean the listener
      }
    };
  }, [loadConfiguration, getReturns]);

  useEffect(() => {
    creatorDisplayFetchStartedRef.current.clear();
    setCreatorDisplayByStored({});
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || uniqueCreatorKeys.length === 0) return;
    for (const stored of uniqueCreatorKeys) {
      if (creatorDisplayFetchStartedRef.current.has(stored)) continue;
      creatorDisplayFetchStartedRef.current.add(stored);
      void resolveOrderCreatorDisplay(organizationId, stored).then(info => {
        if (!mountedRef.current) return;
        setCreatorDisplayByStored(prev =>
          prev[stored] !== undefined ? prev : { ...prev, [stored]: info }
        );
      });
    }
  }, [organizationId, uniqueCreatorKeys]);

  const handleAcceptReturn = useCallback(
    async (returnObj: Return) => {
      if (!mountedRef.current) return;

      showConfirmation(
        {
          title: 'Aprobar Devolución',
          message: `¿Estás seguro de que quieres aprobar la devolución de ${
            returnObj.clientName
          } por ${formatMoney(returnObj.total)}?`,
          confirmText: 'Aprobar',
          cancelText: 'Cancelar',
        },
        async () => {
          if (!mountedRef.current) return;

          showLoading('Aprobando Devolución');

          try {
            await approveReturnServer(organizationId, returnObj.id);
            addToast('Devolución Aprobada!');
            // List will update automatically via Firebase snapshot
          } catch (e: any) {
            console.log(e);
            const errorMessage =
              e?.response?.data?.detail || e?.message || 'Algo salió mal';
            addToast(errorMessage);
          } finally {
            hideLoading();
          }
        }
      );
    },
    [addToast, showConfirmation, organizationId, getReturns, formatMoney]
  );

  const handleDeleteReturn = useCallback(
    async (returnObj: Return) => {
      if (!mountedRef.current) return;

      const confirmCallback = async () => {
        if (!mountedRef.current) return;

        showLoading('Moviendo a Papelera');

        try {
          await deleteReturnServer(organizationId, returnObj.id, true);
          addToast('Devolución movida a papelera');
          // List will update automatically via Firebase snapshot
        } catch (e) {
          console.log(e);
          addToast(mapReturnDeleteError(e));
        } finally {
          hideLoading();
        }
      };

      const financialWarning =
        returnObj.approvalState === 'approved'
          ? ' Esta devolución ya fue aprobada: al eliminarla se revertirá el inventario, se revertirá la nota de crédito aplicada a CxC y se anulará cualquier saldo a favor no utilizado. Luego no podrá restaurarse; si fue un error, deberás crear una nueva devolución.'
          : '';

      showConfirmation(
        {
          title: 'Mover a Papelera',
          message: `¿Estás seguro de que quieres mover la devolución de ${
            returnObj.clientName
          } por ${formatMoney(
            returnObj.total
          )} a la papelera?${
            returnObj.approvalState === 'approved'
              ? ''
              : ' Podrás restaurarla más tarde.'
          }${financialWarning}`,
          confirmText: 'Mover a Papelera',
          cancelText: 'Cancelar',
        },
        confirmCallback
      );
    },
    [addToast, showConfirmation, organizationId, getReturns, formatMoney]
  );

  const handleDenyReturn = useCallback(
    async (returnObj: Return) => {
      if (!mountedRef.current) return;

      showConfirmation(
        {
          title: 'Rechazar Devolución',
          message: `¿Estás seguro de que quieres rechazar la devolución de ${
            returnObj.clientName
          } por ${formatMoney(returnObj.total)}?`,
          confirmText: 'Rechazar',
          cancelText: 'Cancelar',
        },
        async () => {
          if (!mountedRef.current) return;

          showLoading('Rechazando Devolución');

          try {
            await rejectReturnServer(organizationId, returnObj.id);
            addToast('Devolución Rechazada!');
            // List will update automatically via Firebase snapshot
          } catch (e) {
            console.log(e);
            addToast(mapReturnDeleteError(e));
          } finally {
            hideLoading();
          }
        }
      );
    },
    [addToast, showConfirmation, organizationId, getReturns]
  );

  const handleViewReturnModal = useCallback((returnObj: Return) => {
    setSelectedReturnForView(returnObj);
    setShowReturnView(true);
  }, []);

  const handleCloseReturnView = useCallback(() => {
    setShowReturnView(false);
    setSelectedReturnForView(null);
  }, []);

  const loadMoreReturns = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoadMorePending(true);

    try {
      const result = await loadReturns(organizationId, search, lastDocument, 10);
      if (mountedRef.current) {
        setReturns(prevReturns => [...prevReturns, ...result.returns]);
        setLoadMore(result.hasMore);
        setLastDocument(result.lastDoc);
        setPending(false);
        setLoadMorePending(false);
      }
    } catch (e) {
      console.log(e);
      if (mountedRef.current) {
        setLoadMorePending(false);
        setLoadMore(false);
      }
    }
  }, [organizationId, search, lastDocument]);

  return (
    <Container fluid className="fade-in p-0 p-md-4">
      {isLoading ? <Loading /> : null}

      <SubscriptionGuard organization={organization}>
        {/* Header Section */}
        <div className="white-card mb-4">
          <div className="text-center text-md-start">
            <h2 className="fw-bold mb-3">Devoluciones</h2>
            {!returnApprovalRequired && (
              <Badge bg="info" className="mb-3">
                Aprobación automática activada
              </Badge>
            )}
            {showProfitPlusReturnsInfo && (
              <Alert variant="info" className="mb-0 text-start small">
                <strong>Profit Plus 2k8:</strong> por ahora registre la devolución primero en
                Profit y luego emúlela aquí para mantener los datos alineados. La actualización
                de inventario de los productos desde Profit se encola al aprobar la devolución
                (si la aprobación es automática, al guardarla). Una versión futura podría
                automatizar este flujo.
              </Alert>
            )}
          </div>
        </div>

        {screen === 0 ? (
          <>
            {/* Search and Actions Section */}
            <div className="white-card mb-4">
              <Row className="g-3">
                <Col xs={12} md={8}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FiSearch />
                    </InputGroup.Text>
                    <FormControl
                      type="text"
                      value={search}
                      placeholder="Buscar devoluciones por cliente o número..."
                      onChange={e => {
                        if (mountedRef.current) {
                          setSearch(e.currentTarget.value);
                        }
                      }}
                      onKeyPress={(
                        e: React.KeyboardEvent<HTMLInputElement>
                      ) => {
                        if (e.key === 'Enter') {
                          getReturns();
                        }
                      }}
                    />
                    <Button variant="outline-secondary" onClick={getReturns}>
                      Buscar
                    </Button>
                  </InputGroup>
                </Col>
                <Col xs={12} md={4}>
                  <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
                    <Button
                      variant="outline-secondary"
                      onClick={() => setScreen(1)}
                      title="Ver devoluciones eliminadas"
                      className="w-100 w-sm-auto"
                    >
                      <FiTrash2 className="me-1" />
                      <span className="d-none d-sm-inline">Papelera</span>
                      <span className="d-sm-none">Papelera</span>
                    </Button>
                    <RequirePanelWrite area={PANEL_PERMISSIONS.ORDERS}>
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (!canWriteOrders) return;
                          setShowForm(true);
                        }}
                        className="w-100 w-sm-auto"
                      >
                        <FiPlus className="me-1" />
                        <span className="d-none d-sm-inline">
                          Crear Devolución
                        </span>
                        <span className="d-sm-none">Crear</span>
                      </Button>
                    </RequirePanelWrite>
                  </div>
                </Col>
              </Row>
            </div>

            <PanelReadOnlyBanner area={PANEL_PERMISSIONS.ORDERS} />

            <div className="d-flex justify-content-end mb-3">
              <Form.Check
                type="switch"
                id="toggle-all-return-columns"
                label="Ver todas las columnas"
                checked={showAllColumns}
                onChange={e => setShowAllColumns(e.currentTarget.checked)}
              />
            </div>

            {pending === true ? (
              <div className="d-flex justify-content-start mt-2">
                <Spinner animation="border" />
              </div>
            ) : returns.length > 0 ? (
              <>
                {/* Mobile Card View */}
                <div className="d-block d-lg-none">
                  {returns.map((r, i) => {
                    const sourceSalesLabel = getReturnSourceSalesDisplay(r);
                    return (
                    <Card
                      key={r.id || i}
                      className={`mb-3 border-0 shadow-sm ${styles.returnCard} ${listStyles.clickableCard}`}
                      onClick={() => handleViewReturnModal(r)}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                          <div className="min-w-0">
                            <div className={styles.referenceMain}>
                              {getReturnReference(r)}
                            </div>
                            <h6 className={`mb-1 mt-1 ${styles.primaryLine}`}>
                              {r.clientName}
                            </h6>
                            <div className={styles.secondaryLine}>
                              {renderCreatorSalesPerson(r.creatorSalesPerson)}
                            </div>
                            {sourceSalesLabel && (
                              <div className={styles.secondaryLine}>
                                Venta {sourceSalesLabel}
                              </div>
                            )}
                          </div>
                          <Badge
                            bg={getApprovalStateInfo(r.approvalState).variant}
                            className={styles.statusBadge}
                          >
                            {getApprovalStateInfo(r.approvalState).label}
                          </Badge>
                        </div>

                        <div className={`mb-3 ${styles.mobileMetaGrid}`}>
                          <div className={styles.mobileMetaItem}>
                            <span className={styles.mobileMetaLabel}>
                              Registro
                            </span>
                            <span className={styles.mobileMetaValue}>
                              {formatDateEsFromFirestore(r.created)}
                              <span className={`d-block ${styles.dateTimeMeta}`}>
                                {formatTimeEsFromFirestore(r.created)}
                              </span>
                            </span>
                          </div>
                          <div className={styles.mobileMetaItem}>
                            <span className={styles.mobileMetaLabel}>Pago</span>
                            <span className={styles.mobileMetaValue}>
                              {r.paymentMethod}
                              {r.paymentCurrency
                                ? ` · ${r.paymentCurrency}`
                                : ''}
                            </span>
                          </div>
                          <div className={styles.mobileMetaItem}>
                            <span className={styles.mobileMetaLabel}>
                              Total
                            </span>
                            <span className={styles.mobileTotal}>
                              {formatMoney(r.total)}
                            </span>
                          </div>
                        </div>

                        <ReturnRowActions
                          returnObj={r}
                          returnApprovalRequired={returnApprovalRequired}
                          layout="mobile"
                          canWrite={canWriteOrders}
                          onView={handleViewReturnModal}
                          onApprove={handleAcceptReturn}
                          onReject={handleDenyReturn}
                          onDelete={handleDeleteReturn}
                        />
                      </Card.Body>
                    </Card>
                    );
                  })}
                </div>
                {loadMore === true && (
                  <div className="d-flex justify-content-center mt-3 d-lg-none">
                    {loadMorePending ? (
                      <Spinner animation="border" />
                    ) : (
                      <Button variant="primary" onClick={loadMoreReturns}>
                        Cargar más
                      </Button>
                    )}
                  </div>
                )}

                {/* Desktop Table View */}
                <Card className={`d-none d-lg-block border-0 shadow-sm mt-2 ${listStyles.tableCard} ${styles.returnsTableCard}`}>
                  <Card.Body className="p-0">
                    <div className={`table-responsive ${listStyles.tableWrapper} ${styles.returnsTableScroll}`}>
                  <Table
                    responsive
                    hover
                    borderless
                    className={`${listStyles.dataTable} ${
                      showAllColumns ? listStyles.noWrap : ''
                    } mb-0`}
                  >
                    <thead>
                      <tr>
                        <th className={listStyles.referenceCol}>Devolución</th>
                        <th>Cliente</th>
                        <th>Vendedor</th>
                        {showAllColumns && (
                          <>
                            <th>Forma de pago</th>
                            <th>Moneda</th>
                          </>
                        )}
                        <th>Registro</th>
                        <th>Estado</th>
                        {showAllColumns && (
                          <>
                            <th>Orden original</th>
                            <th className={listStyles.moneyHeader}>Subtotal</th>
                            <th className={listStyles.moneyHeader}>Impuestos</th>
                          </>
                        )}
                        <th className={listStyles.moneyHeader}>Total</th>
                        <th
                          className={`${listStyles.stickyRightHeader} ${listStyles.actionsCol} ${styles.actionsColNarrow}`}
                        >
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {returns.map((r, i) => {
                        const sourceSalesLabel = getReturnSourceSalesDisplay(r);
                        return (
                        <tr
                          key={r.id || i}
                          className={listStyles.clickableRow}
                          onClick={() => handleViewReturnModal(r)}
                        >
                          <td className={`${listStyles.referenceCol} ${styles.referenceCell}`}>
                            <div className={styles.referenceMain}>
                              {getReturnReference(r)}
                            </div>
                            {sourceSalesLabel && (
                              <div className={styles.secondaryLine}>
                                Venta {sourceSalesLabel}
                              </div>
                            )}
                          </td>
                          <td className={styles.clientCell}>
                            <div className={styles.primaryLine}>{r.clientName}</div>
                          </td>
                          <td>{renderCreatorSalesPerson(r.creatorSalesPerson)}</td>
                          {showAllColumns && (
                            <>
                              <td className="text-muted">{r.paymentMethod}</td>
                              <td>{r.paymentCurrency || '—'}</td>
                            </>
                          )}
                          <td className={styles.dateCell}>
                            <div>{formatDateEsFromFirestore(r.created)}</div>
                            <div className={styles.dateTimeMeta}>
                              {formatTimeEsFromFirestore(r.created)}
                            </div>
                          </td>
                          <td>
                            <Badge
                              bg={getApprovalStateInfo(r.approvalState).variant}
                              className={styles.statusBadge}
                            >
                              {getApprovalStateInfo(r.approvalState).label}
                            </Badge>
                          </td>
                          {showAllColumns && (
                            <>
                              <td>{sourceSalesLabel || '—'}</td>
                              <td className={listStyles.moneyCell}>
                                {formatMoney(r.subtotal)}
                              </td>
                              <td className={listStyles.moneyCell}>
                                {formatMoney(getDisplayIvaAmount(r))}
                              </td>
                            </>
                          )}
                          <td className={`${listStyles.moneyCell} ${styles.totalCell}`}>
                            {formatMoney(r.total)}
                          </td>
                          <td
                            className={`${listStyles.stickyRightCell} ${listStyles.actionsCol} ${styles.actionsColNarrow} ${styles.actionsStickyCell}`}
                            onClick={e => e.stopPropagation()}
                          >
                            <ReturnRowActions
                              returnObj={r}
                              returnApprovalRequired={returnApprovalRequired}
                              layout="table"
                              canWrite={canWriteOrders}
                              onView={handleViewReturnModal}
                              onApprove={handleAcceptReturn}
                              onReject={handleDenyReturn}
                              onDelete={handleDeleteReturn}
                            />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                    </div>
                  </Card.Body>
                </Card>
                {loadMore === true && (
                  <div className="d-none d-lg-flex justify-content-center mt-3">
                    {loadMorePending ? (
                      <Spinner animation="border" />
                    ) : (
                      <Button variant="primary" onClick={loadMoreReturns}>
                        Cargar más
                      </Button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p>No hay devoluciones</p>
            )}
          </>
        ) : null}

        {screen === 1 ? (
          <ReturnTrashBin
            device={device}
            addToast={addToast}
            navStatus={navStatus}
            organizationId={organizationId}
            onBack={() => setScreen(0)}
          />
        ) : null}

        <ReturnForm
          show={showForm}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            loadConfiguration();
            getReturns();
          }}
          organizationId={organizationId}
          addToast={addToast}
        />

        {showReturnView && selectedReturnForView && (
          <ReturnView
            show={showReturnView}
            onClose={handleCloseReturnView}
            returnObj={selectedReturnForView}
            referenceCurrency={referenceCurrency}
            currency={currency}
          />
        )}
      </SubscriptionGuard>
    </Container>
  );
}
